import { formatMoney } from "../../lib/money";

import { LineMiniChart } from "./svgPrimitives";

interface EquityCurvePoint {
  trade_date: string;
  cumulative_pnl_amount: string;
}

interface EquityCurveChartProps {
  points: ReadonlyArray<EquityCurvePoint>;
  currency?: string;
  fill?: boolean;
  // Line color. Defaults to the primary (burnt-orange) accent; the dashboard
  // hero passes "success" so cumulative P&L reads green.
  tone?: "primary" | "success";
  // When true, draw a left "$" value axis and a bottom date axis around the
  // line (dashboard Cumulative P&L hero). The mini line's own corner labels are
  // suppressed so the axis reads cleanly.
  axis?: boolean;
  // When true, prepend a $0 baseline point so the curve always starts at zero
  // rather than the first session's cumulative total.
  baselineZero?: boolean;
}

function compactMoney(value: number, currency: string): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(value);
}

function shortDate(isoDate: string): string {
  const [y, m, d] = isoDate.split("-").map(Number);
  if (!y || !m || !d) return isoDate;
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(y, m - 1, d)));
}

export function EquityCurveChart({
  points,
  currency = "USD",
  fill,
  tone = "primary",
  axis = false,
  baselineZero = false,
}: EquityCurveChartProps) {
  // Optionally anchor the curve at $0: prepend a zero point carrying the first
  // session's date so the line rises from the baseline.
  const seriesValues = points.map((p) => Number(p.cumulative_pnl_amount));
  const seriesLabels = points.map((p) => p.trade_date);
  const values =
    baselineZero && points.length > 0 ? [0, ...seriesValues] : seriesValues;
  const labels =
    baselineZero && points.length > 0
      ? [seriesLabels[0] ?? "", ...seriesLabels]
      : seriesLabels;

  const line = (
    <LineMiniChart
      values={values}
      testId="equity-curve-chart"
      tone={tone}
      labels={labels}
      formatValue={(v) => formatMoney(v.toFixed(2), { currency })}
      fill={fill}
      hideMinMax={axis}
    />
  );

  if (!axis || values.length === 0) {
    return line;
  }

  // The y-axis spans the same zero-anchored domain the line uses (min..max,
  // both clamped to include 0), so the tick labels line up with the curve.
  const max = Math.max(0, ...values);
  const min = Math.min(0, ...values);
  const mid = (max + min) / 2;
  const first = labels[0];
  const last = labels[labels.length - 1];
  const middle = labels[Math.floor((labels.length - 1) / 2)];

  return (
    <div className="flex h-full w-full flex-col">
      <div className="flex min-h-0 flex-1">
        <div className="flex w-12 shrink-0 flex-col justify-between py-1 pr-1 text-right text-[10px] tabular-nums text-on-surface-muted">
          <span>{compactMoney(max, currency)}</span>
          <span>{compactMoney(mid, currency)}</span>
          <span>{compactMoney(min, currency)}</span>
        </div>
        <div className="min-w-0 flex-1">{line}</div>
      </div>
      <div className="flex justify-between pl-12 pt-1 text-[10px] tabular-nums text-on-surface-muted">
        <span>{first ? shortDate(first) : ""}</span>
        {labels.length > 2 ? <span>{middle ? shortDate(middle) : ""}</span> : null}
        <span>{last ? shortDate(last) : ""}</span>
      </div>
    </div>
  );
}
